import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useCallback, useState } from 'react';

import { Logger } from '@/lib/logs/logger';

type UsePdfExportArgs = {
  filename: string;
  orientation?: 'landscape' | 'portrait';
  scale?: number;
  onError?: (error: unknown) => void;
};

/**
 * Generic hook for client-side PDF export.
 *
 * Mark the root container with a `ref`, then annotate children:
 *   - `data-pdf-page`         — each element becomes one PDF page (landscape A4).
 *   - `data-pdf-page-header`  — cloned and prepended to every page; keep it
 *                               invisible in the UI with `style={{ display: 'none' }}`.
 */
export const usePdfExport = ({ filename, orientation = 'landscape', scale = 2, onError }: UsePdfExportArgs) => {
  const handleError = useCallback(
    (error: unknown) => {
      if (onError) {
        onError(error);
        Logger.error(`PDF export failed', ${error instanceof Error ? error.message : String(error)}`);
      }
    },
    [onError],
  );
  const [isExporting, setIsExporting] = useState(false);

  const exportToPdf = useCallback(
    async (containerElement: HTMLElement | null) => {
      if (!containerElement) return;

      setIsExporting(true);

      const pageElements = Array.from(containerElement.querySelectorAll('[data-pdf-page]')) as HTMLElement[];
      const headerEl = containerElement.querySelector('[data-pdf-page-header]') as HTMLElement | null;

      if (pageElements.length === 0) return;

      // new-cap: alias to a capitalised name so the linter is satisfied.
      const JsPDF = jsPDF;
      const pdf = new JsPDF({ orientation, unit: 'mm', format: 'a4' });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const renderPage = async (pageEl: HTMLElement, isFirstPage: boolean): Promise<void> => {
        const pageWidth = pageEl.getBoundingClientRect().width;

        // Build an off-screen wrapper that mirrors the page at its natural width.
        const wrapper = document.createElement('div');
        wrapper.style.cssText = `
          position: fixed;
          top: -99999px;
          left: -99999px;
          width: ${pageWidth}px;
          background: white;
          padding: 16px;
          box-sizing: border-box;
        `;

        // Prepend a clone of the sticky header on every page except the first.
        if (headerEl && !isFirstPage) {
          const headerClone = headerEl.cloneNode(true) as HTMLElement;
          // The original is hidden in the DOM; make the clone visible.
          headerClone.style.display = 'block';
          headerClone.style.marginBottom = '12px';
          // Remove any border that was used for UI separation — not needed in PDF.
          const innerDiv = headerClone.firstElementChild as HTMLElement | null;
          if (innerDiv) innerDiv.style.borderBottom = 'none';
          wrapper.appendChild(headerClone);
        }

        // Append the page content clone.
        const pageClone = pageEl.cloneNode(true) as HTMLElement;
        wrapper.appendChild(pageClone);

        document.body.appendChild(wrapper);

        try {
          const canvas = await html2canvas(wrapper, {
            scale,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            windowWidth: wrapper.offsetWidth,
          });

          const imgData = canvas.toDataURL('image/png');

          // Fit the canvas into the PDF page while preserving aspect ratio.
          const canvasAspect = canvas.width / canvas.height;
          const pdfAspect = pdfWidth / pdfHeight;
          let imgW = pdfWidth;
          let imgH = pdfHeight;
          if (canvasAspect > pdfAspect) {
            imgH = pdfWidth / canvasAspect;
          } else {
            imgW = pdfHeight * canvasAspect;
          }
          const x = (pdfWidth - imgW) / 2;
          const y = (pdfHeight - imgH) / 2;

          if (!isFirstPage) pdf.addPage();
          pdf.addImage(imgData, 'PNG', x, y, imgW, imgH);
        } finally {
          document.body.removeChild(wrapper);
        }
      };

      try {
        // Chain pages sequentially (no for-of, no await-in-loop).
        await pageElements.reduce(
          (chain, pageEl, idx) => chain.then(() => renderPage(pageEl, idx === 0)),
          Promise.resolve(),
        );

        pdf.save(filename);
      } catch (error) {
        handleError(error);
      } finally {
        setIsExporting(false);
      }
    },
    [filename, orientation, scale, handleError],
  );

  return { exportToPdf, isExporting };
};
